import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Paper,
  TextField,
  Typography,
  Box,
  Button,
  MenuItem,
  Divider,
  Stack,
  InputLabel,
  FormControl,
  Select,
  CircularProgress,
  FormHelperText,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  FileDownload as ExportIcon,
  FileUpload as ImportIcon
} from "@mui/icons-material"
import { CONSTANTS } from "../../../../library/common/constant";
import styles from "./AddNewTirth.module.scss";
import { api } from "../../../../services";
import MetaFields from "../../../../library/common/MetaFields";
import MultiInput from "../../../../library/common/MultiInput";
import ReactQuillEditor from "../../../../library/component/ReactQuillEditor/ReactQuillEditor";
import { debounce } from "lodash";
import FileUploadPreview from "../../../../library/common/FileUploadPreview";
import SocialMediaField from "../../../../library/common/SocialMediaField";
import { throttle } from "lodash";
import BasicInformationFields from "../../../../library/common/BasicInformationFields";
import CustomDialog from "../../../../library/common/CustomDialog";
import { exportToJson, importJsonDraft } from "../../../../library/common/exportImportDraft";

const cleanDraftData = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof File) {
    return {
      name: obj.name,
      size: obj.size,
      type: obj.type,
      isDraftPlaceholder: true,
    };
  }
  if (Array.isArray(obj)) {
    return obj
      .map(item => cleanDraftData(item))
      .filter(item => item !== undefined);
  }
  if (typeof obj === "object") {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedVal = cleanDraftData(value);
      if (cleanedVal !== undefined) {
        cleaned[key] = cleanedVal;
      }
    }
    return cleaned;
  }
  return obj;
};

const useFileUpload = (setUpdatedData, setErrors) => {
  const handleFileChange = useCallback(
    (e) => {
      const { name, files, multiple } = e.target;
      const maxFiles = 10;

      if (files?.length > 0) {
        const fileArray = [...files];
        const maxSize = 10 * 1024 * 1024;

        const validFiles = fileArray.filter((file) => file.size <= maxSize);
        const invalidFiles = fileArray.filter((file) => file.size > maxSize);

        if (invalidFiles.length > 0 && setErrors) {
          setErrors((prev) => ({
            ...prev,
            [name]: `File(s) ${invalidFiles
              .map((f) => f.name)
              .join(", ")} are too large (max 10MB).`,
          }));
        } else if (setErrors) {
          setErrors((prev) => ({ ...prev, [name]: null }));
        }

        if (validFiles.length === 0) return;
        setUpdatedData((prev) => {
          if (multiple) {
            const existingFiles = prev[name] || [];

            const availableSlots = maxFiles - existingFiles?.length || 0;

            if (availableSlots <= 0) {
              if (setErrors) {
                setErrors((e) => ({
                  ...e,
                  [name]: `You can only upload a maximum of ${maxFiles} items.`,
                }));
              }
              return prev;
            }

            const filesToAdd = validFiles.slice(0, availableSlots);

            if (validFiles.length > availableSlots) {
              if (setErrors) {
                setErrors((e) => ({
                  ...e,
                  [name]: `You can only add ${availableSlots} more items. ${validFiles.length - availableSlots
                    } files were not added.`,
                }));
              }
            }
            return {
              ...prev,
              [name]: [...existingFiles, ...filesToAdd],
            };
          } else {
            return {
              ...prev,
              [name]: validFiles[0],
            };
          }
        });
      } else if (!multiple) {
        setUpdatedData((prev) => ({ ...prev, [name]: null }));
      }
    },
    [setUpdatedData, setErrors]
  );

  return { handleFileChange };
};

const {
  ADD_TIRTH_TITLE,
  ADD_TIRTH_DESCRIPTION,
  LOCALTION_DETAILS_SECTION,
  ADDRESS,
  ADDRESS_HINDI,
  PINCODE,
  COUNTRY,
  DISTRICT,
  STATE,
  META_DETAILS,
  UPDATE_TIRTH_BUTTON,
  ADD_NEW_TIRTH_BUTTON,
  THROTTLE_DURATION,
  ACCEPTED_IMAGE_TYPES,
} = CONSTANTS;

const TirthForm = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEditMode = Boolean(slug);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [sector, setSector] = useState([]);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [apiError, setApiError] = useState("");
  const [updatedData, setUpdatedData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef(null);
  const importInputRef = useRef(null);

  // Draft and Toast states
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  const { handleFileChange } = useFileUpload(setUpdatedData, setErrors);

  useEffect(() => {
    const fetchTirthDetails = async () => {
      try {
        const { data } = await api.get(`/tirths/${slug}`);

        setFormData({
          ...data,
          galleryUrls: data?.gallery,
          gallery: [],
          sect: data?.sect?.map((s) => s._id) || [],
          coordinates: {
            latitude: data?.coordinates?.coordinates?.[0]?.toString() || "",
            longitude: data?.coordinates?.coordinates?.[1]?.toString() || "",
          },
          country: data?.country?._id || "",
          state: data?.state?._id || "",
          district: data?.district?._id || "",
        });
        setLoading(false);
      } catch (error) {
        setApiError("Tirth not found");
        setLoading(false);
      }
    };

    if (isEditMode) {
      fetchTirthDetails();
    }
  }, [slug, isEditMode]);

  const getFieldValue = useCallback(
    (fieldPath) => {
      const keys = fieldPath.split(".");

      let value = keys.reduce((acc, key) => acc && acc[key], updatedData);

      if (value === undefined) {
        value = keys.reduce((acc, key) => acc && acc[key], formData);
      }

      return value;
    },
    [updatedData, formData]
  );

  const validateSlug = useCallback(async (slugVal) => {
    if (updatedData?.slug === formData?.slug) return true;
    try {
      const { data } = await api.get(`slug/tirth/${slugVal}`);
      if (!data) setErrors((prev) => ({ ...prev, slug: "Slug is not unique" }));
      return data;
    } catch {
      setErrors((prev) => ({ ...prev, slug: "Slug validation failed" }));
      return false;
    }
  }, [updatedData, formData]);

  const debouncedValidateSlug = useMemo(
    () =>
      debounce(async (slugVal) => {
        await validateSlug(slugVal);
      }, 500),
    [validateSlug]
  );

  useEffect(() => {
    return () => {
      debouncedValidateSlug.cancel();
    };
  }, [debouncedValidateSlug]);

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const { data } = await api.get(`/sects`);
        setSector(data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchSectors();
  }, []);

  const focusFirstError = (newErrors) => {
    const firstErrorKey = Object.keys(newErrors)[0];
    if (!firstErrorKey) return;

    let element = document.getElementsByName(firstErrorKey)[0];
    if (!element) {
      element = document.getElementById(firstErrorKey);
    }
    if (!element) {
      element = document.querySelector(`[name="${firstErrorKey}"]`);
    }
    if (!element) {
      element = document.querySelector(
        `input[name*="${firstErrorKey}"], select[name*="${firstErrorKey}"], textarea[name*="${firstErrorKey}"]`
      );
    }

    if (element) {
      element.focus();
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };


  // Export current page tirths as JSON
  const handleExport = () => {
    const ok = exportToJson(updatedData, "tirths");
    setToast({
      open: true,
      message: ok ? "Tirths exported successfully!" : "Export failed.",
      severity: ok ? "success" : "error",
    });
  };

  // Import JSON draft
  const handleImportClick = () => importInputRef.current?.click();
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = null;
    importJsonDraft(
      file,
      "draft_tirth",
      navigate,
      "/tirth/add",
      (msg) => setToast({ open: true, message: msg, severity: "success" }),
      (msg) => setToast({ open: true, message: msg, severity: "error" })
    );
  };

  const handleApiError = (error) => {
    const data = error.response?.data;
    const message = data?.message || error.message || "An error occurred.";
    const errorsPayload = data?.errors;

    let displayMessage = message;
    if (errorsPayload) {
      if (typeof errorsPayload === "string") {
        displayMessage = errorsPayload;
      } else if (typeof errorsPayload === "object") {
        const messages = [];
        for (const [key, value] of Object.entries(errorsPayload)) {
          if (value && typeof value === "object") {
            messages.push(value.message || JSON.stringify(value));
          } else {
            messages.push(String(value));
          }
        }
        displayMessage = messages.join(", ");
      }
    }

    const isSizeError = /size|large|limit|upload/i.test(displayMessage) || /size|large|limit|upload/i.test(message);
    if (isSizeError) {
      const isImage = /image|cover|authorImage|featured/i.test(displayMessage) || /image|cover|authorImage|featured/i.test(message) || /image|cover|authorImage|featured/i.test(JSON.stringify(errorsPayload));
      const isFile = /file|pdf/i.test(displayMessage) || /file|pdf/i.test(message) || /file|pdf/i.test(JSON.stringify(errorsPayload));
      if (isImage) {
        displayMessage = "Use URL image or image size is large";
      } else if (isFile) {
        displayMessage = "Use URL file or file size is large";
      } else {
        displayMessage = "Use URL image or file size is large";
      }
    }

    setToast({
      open: true,
      message: displayMessage,
      severity: "error",
    });

    if (errorsPayload) {
      const parsedErrors = {};
      if (typeof errorsPayload === "string") {
        const parts = errorsPayload.split(",");
        parts.forEach((part) => {
          const match = part.match(/"([^"]+)"\s+(.+)/);
          if (match) {
            const field = match[1];
            const desc = match[2];
            parsedErrors[field] = `Server: "${field}" ${desc}`;
          }
        });
      } else if (typeof errorsPayload === "object") {
        for (const [key, value] of Object.entries(errorsPayload)) {
          if (value && typeof value === "object") {
            parsedErrors[key] = value.message || String(value);
          } else {
            parsedErrors[key] = String(value);
          }
        }
      }
      if (Object.keys(parsedErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...parsedErrors }));
        focusFirstError(parsedErrors);
      }
    } else if (message.includes("Duplicate Key Error") || error.response?.status === 409) {
      if (message.includes("slug")) {
        setErrors((prev) => ({ ...prev, slug: "Slug already exists." }));
        focusFirstError({ slug: true });
      }
    }
  };

  const validateForm = async () => {
    const newErrors = {};

    const slugVal = (getFieldValue("slug") || "").trim();

    if (!slugVal) {
      newErrors["slug"] = "Slug is required";
    } else if (!/^[A-Za-z0-9_-]+$/.test(slugVal)) {
      newErrors["slug"] =
        "Slug can only contain letters, numbers, hyphens, and underscores";
    } else await validateSlug(slugVal);

    if (!(getFieldValue("title.en") || "").trim()) {
      newErrors["title.en"] = "English title is required";
    }

    if (!(getFieldValue("title.hi") || "").trim()) {
      newErrors["title.hi"] = "Hindi title is required";
    }

    if (!(getFieldValue("shortDescription.en") || "").trim()) {
      newErrors["shortDescription.en"] =
        "Short description (English) is required";
    }
    if (!(getFieldValue("shortDescription.hi") || "").trim()) {
      newErrors["shortDescription.hi"] =
        "Short description (Hindi) is required";
    }

    if (!(getFieldValue("address.en") || "").trim()) {
      newErrors["address.en"] = "Address (English) is required";
    }
    if (!(getFieldValue("address.hi") || "").trim()) {
      newErrors["address.hi"] = "Address (Hindi) is required";
    }

    const pincodeVal = (getFieldValue("pincode") || "").trim();
    if (!pincodeVal) {
      newErrors["pincode"] = "Pincode is required";
    } else if (!/^\d{6}$/.test(pincodeVal)) {
      newErrors["pincode"] = "Pincode must be a 6-digit number";
    }

    if (!getFieldValue("country")) {
      newErrors["country"] = "Country is required";
    }
    if (!getFieldValue("state")) {
      newErrors["state"] = "State is required";
    }
    if (!getFieldValue("district")) {
      newErrors["district"] = "District is required";
    }

    const lat = (getFieldValue("coordinates.latitude") || "").trim();
    if (!lat) {
      newErrors["coordinates.latitude"] = "Latitude is required";
    } else if (isNaN(lat)) {
      newErrors["coordinates.latitude"] = "Latitude must be a number";
    } else if (Number(lat) < -90 || Number(lat) > 90) {
      newErrors["coordinates.latitude"] = "Latitude must lie between -90 & +90";
    }

    const lng = (getFieldValue("coordinates.longitude") || "").trim();
    if (!lng) {
      newErrors["coordinates.longitude"] = "Longitude is required";
    } else if (isNaN(lng)) {
      newErrors["coordinates.longitude"] = "Longitude must be a number";
    } else if (Number(lng) < -180 || Number(lng) > 180) {
      newErrors["coordinates.longitude"] =
        "Longitude must lie between -180 & 180";
    }

    if (!(getFieldValue("sect") || []).length) {
      newErrors["sect"] = "Sector is required";
    }

    // Cover Image Draft Placeholder Check
    const coverImage = updatedData["coverImage"];
    if (coverImage && coverImage.isDraftPlaceholder) {
      newErrors["coverImageUrl"] = "Please re-upload the cover image (draft files must be re-selected)";
    }

    // Gallery Draft Placeholder Check
    const gallery = getFieldValue("gallery") || [];
    if (gallery.some((item) => item && item.isDraftPlaceholder)) {
      newErrors["gallery"] = "Please re-upload draft files in gallery (draft files must be re-selected)";
    }

    // URL field validations
    const locationLink = (getFieldValue("locationLink") || "").trim();
    if (locationLink && !/^https?:\/\/.+/.test(locationLink)) {
      newErrors["locationLink"] = "Please provide a valid Location URL";
    }

    const donationLink = (getFieldValue("donationInfo.donationLink") || "").trim();
    if (donationLink && !/^https?:\/\/.+/.test(donationLink)) {
      newErrors["donationInfo.donationLink"] = "Please provide a valid Donation URL";
    }

    const contactWebsite = (getFieldValue("contactDetails.website") || "").trim();
    if (contactWebsite && !/^https?:\/\/.+/.test(contactWebsite)) {
      newErrors["contactDetails.website"] = "Please provide a valid Website URL";
    }

    const metafieldsCanonical = (getFieldValue("metafields.canonical") || "").trim();
    if (metafieldsCanonical && !/^https?:\/\/.+/.test(metafieldsCanonical)) {
      newErrors["metafields.canonical"] = "Please provide a valid URL for canonical link";
    }

    const metafieldsImage = (getFieldValue("metafields.image") || "").trim();
    if (metafieldsImage && !/^https?:\/\/.+/.test(metafieldsImage)) {
      newErrors["metafields.image"] = "Please provide a valid URL for metafields image";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      setToast({ open: true, message: "Please correct the errors in the form.", severity: "error" });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = useCallback(
    (eOrName, maybeValue) => {
      let name, value;

      if (typeof eOrName === "object" && eOrName.target) {
        const {
          name: inputName,
          value: inputValue,
          type,
          checked,
        } = eOrName.target;
        name = inputName;
        value = type === "checkbox" ? checked : inputValue;
      } else {
        name = eOrName;
        value = maybeValue;
      }

      if (name === "slug" && typeof value === "string") {
        value = value.replace(/\s+/g, "-");
      }

      const keys = name.split(".");

      setUpdatedData((prev) => {
        const updated = { ...prev };
        let current = updated;

        for (let i = 0; i < keys.length - 1; i++) {
          const k = keys[i];
          current[k] = current[k] || {};
          current = current[k];
        }

        current[keys[keys.length - 1]] = value;
        return updated;
      });

      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }

      if (name === "slug") {
        debouncedValidateSlug(value);
      }
    },
    [errors, debouncedValidateSlug]
  );

  const handleRemoveGalleryItem = (indexToRemove) => {
    const currentGallery = getFieldValue("gallery") || [];
    const currentGalleryUrls = getFieldValue("galleryUrls") || [];

    if (indexToRemove < currentGallery.length) {
      const updatedGallery = currentGallery.filter(
        (_, index) => index !== indexToRemove
      );
      setUpdatedData((prev) => ({
        ...prev,
        gallery: updatedGallery,
      }));
    } else {
      const realIndex = indexToRemove - currentGallery.length;
      const updatedGalleryUrls = currentGalleryUrls.filter(
        (_, index) => index !== realIndex
      );
      setUpdatedData((prev) => ({
        ...prev,
        galleryUrls: updatedGalleryUrls,
      }));
    }
  };

  const handleAddGalleryUrl = (url) => {
    if (!url) return;
    const maxFiles = 10;

    setUpdatedData((prev) => {
      const existingItems = prev.gallery
        ? Array.isArray(prev.gallery)
          ? prev.gallery
          : [prev.gallery]
        : [];

      if (existingItems.length >= maxFiles) {
        if (setErrors) {
          setErrors((e) => ({
            ...e,
            gallery: `You can only have a maximum of ${maxFiles} items.`,
          }));
        }
        return prev;
      }

      return {
        ...prev,
        gallery: [...existingItems, url],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) return;

    const cleanPayload = (obj) => {
      if (obj === null || obj === undefined) return undefined;
      if (obj instanceof File) return obj;
      if (Array.isArray(obj)) {
        const cleaned = obj.map(cleanPayload).filter(v => v !== undefined);
        return cleaned.length > 0 ? cleaned : undefined;
      }
      if (typeof obj === "object") {
        const cleaned = {};
        let hasKeys = false;
        for (const [k, v] of Object.entries(obj)) {
          if (v === "" || v === null || v === undefined) {
            continue;
          }
          const cv = cleanPayload(v);
          if (cv !== undefined) {
            cleaned[k] = cv;
            hasKeys = true;
          }
        }
        return hasKeys ? cleaned : undefined;
      }
      return obj;
    };

    const cleanedData = cleanPayload(updatedData) || {};
    const formToSend = new FormData();
    const finalGallery = getFieldValue("gallery") || [];
    finalGallery.forEach((item) => {
      if (item instanceof File) {
        formToSend.append("gallery", item);
      } else if (typeof item === "string" && !item.isDraftPlaceholder) {
        formToSend.append("galleryUrls", item);
      }
    });

    const finalGalleryUrls = getFieldValue("galleryUrls") || [];
    finalGalleryUrls.forEach((item) => {
      if (typeof item === "string") {
        formToSend.append("galleryUrls", item);
      }
    });

    for (const key in cleanedData) {
      if (key === "gallery") {
        continue;
      }
      if (key === "galleryUrls") {
        continue;
      }
      if (key === "coverImage") {
        continue;
      }
      let value = cleanedData[key];

      if (value && value.isDraftPlaceholder) {
        continue;
      }

      if (key === "slug" && typeof value === "string") {
        value = value.replace(/\s+/g, "-").replace(/^-+|-+$/g, "");
      }

      if (key === "metafields" && typeof value === "object") {
        Object.keys(value).forEach((metaKey) => {
          formToSend.append(`metafields.${metaKey}`, value[metaKey]);
        });
        continue;
      }

      if (Array.isArray(value)) {
        if (key === "sect") {
          value.forEach((id) => formToSend.append("sect[]", id));
        } else {
          formToSend.append(key, JSON.stringify(value));
        }
      } else if (typeof value === "object" && !(value instanceof File)) {
        formToSend.append(key, JSON.stringify(value));
      } else {
        formToSend.append(key, value);
      }
    }

    // Explicitly append coverImage file if selected and not a placeholder
    const coverImage = updatedData["coverImage"];
    if (coverImage instanceof File) {
      formToSend.append("coverImage", coverImage);
    }

    if (isEditMode) formToSend.append("verified", true);

    formRef.current = formToSend;

    try {
      if (isEditMode) {
        handleEdit();
      } else {
        handleAdd();
      }
    } catch (error) {
      console.error("Full error object:", error);
      handleApiError(error);
    }
  };

  const returnBack = () => {
    navigate("/tirth/list", { replace: true });
  };

  const handleEdit = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.patch(`/tirths/${formData?._id}`, form, { skipAlert: true });
      returnBack();
    } catch (error) {
      console.error("Failed to save: ", error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleAdd = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.post("/tirths", form, { skipAlert: true });
      localStorage.removeItem("draft_tirth");
      returnBack();
    } catch (error) {
      console.error("Failed to save: ", error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  // Autosave draft effect
  useEffect(() => {
    if (isEditMode || Object.keys(updatedData).length === 0) return;

    const save = () => {
      try {
        const cleaned = cleanDraftData(updatedData);
        localStorage.setItem("draft_tirth", JSON.stringify({
          data: cleaned,
          timestamp: Date.now(),
        }));
      } catch (err) {
        console.error("Failed to save draft:", err);
      }
    };

    const timeout = setTimeout(save, 1000);
    return () => clearTimeout(timeout);
  }, [updatedData, isEditMode]);

  // Load draft check on mount
  useEffect(() => {
    if (isEditMode) return;
    try {
      const stored = localStorage.getItem("draft_tirth");
      if (stored) {
        const { data, timestamp } = JSON.parse(stored);
        const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
        if (Date.now() - timestamp < THIRTY_DAYS) {
          setPendingDraft(data);
          setDraftDialogOpen(true);
        } else {
          localStorage.removeItem("draft_tirth");
        }
      }
    } catch (err) {
      console.error("Failed to check draft:", err);
    }
  }, [isEditMode]);

  const handleRestoreDraft = () => {
    if (pendingDraft) {
      setUpdatedData(pendingDraft);
      setToast({ open: true, message: "Draft restored successfully!", severity: "success" });
    }
    setDraftDialogOpen(false);
    setPendingDraft(null);
  };

  const handleDeleteDraft = () => {
    localStorage.removeItem("draft_tirth");
    setToast({ open: true, message: "Draft deleted.", severity: "info" });
    setDraftDialogOpen(false);
    setPendingDraft(null);
  };

  const handleResetForm = () => {
    setUpdatedData({});
    localStorage.removeItem("draft_tirth");
    setErrors({});
    setToast({ open: true, message: "Form reset and draft cleared.", severity: "info" });
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await api.get(`/countries`);
        setCountries(
          res?.data?.countries?.sort((a, b) =>
            a.name.en.localeCompare(b.name.en)
          ) || []
        );
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await api.get(`/states`);
        setStates(
          res?.data?.states?.sort((a, b) =>
            a.name.en.localeCompare(b.name.en)
          ) || []
        );
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    fetchStates();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className={styles.paperContainer}>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? "Update Tirth" : ADD_TIRTH_TITLE}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {isEditMode
          ? "Update the tirth details below. / नीचे तीर्थ का विवरण अपडेट करें।"
          : ADD_TIRTH_DESCRIPTION}
      </Typography>
      {!isEditMode ? (<Box display="flex" justifyContent="flex-end" gap={2}>
        {/* Hidden import file input */}
        <input
          type="file"
          accept="application/json"
          ref={importInputRef}
          style={{ display: "none" }}
          onChange={handleImportFile}
        />
        <Button
          variant="outlined"
          size="medium"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          title="Export current page as JSON"
        >
          Export
        </Button>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<ImportIcon />}
          onClick={handleImportClick}
          title="Import JSON to add form"
        >
          Import
        </Button>
      </Box>) : null}
      {apiError && <Typography color="error">{apiError}</Typography>}
      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}

        <BasicInformationFields
          updatedData={updatedData}
          formData={formData}
          handleChange={handleChange}
          getFieldValue={getFieldValue}
          errors={errors}
          page="tirth"
        />

        <Divider variant="middle" className={styles.divider} />

        {/* Location Details Section */}
        <Box className={styles.section_container}>
          <Typography variant="h6" gutterBottom>
            {LOCALTION_DETAILS_SECTION}
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label={ADDRESS}
                name="address.en"
                value={getFieldValue("address.en") || ""}
                onChange={handleChange}
                required
                error={!!errors["address.en"]}
                helperText={errors["address.en"]}
              />
              <TextField
                fullWidth
                label={ADDRESS_HINDI}
                name="address.hi"
                value={getFieldValue("address.hi") || ""}
                onChange={handleChange}
                required
                error={!!errors["address.hi"]}
                helperText={errors["address.hi"]}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label={PINCODE}
                name="pincode"
                value={getFieldValue("pincode") || ""}
                onChange={handleChange}
                required
                error={!!errors["pincode"]}
                helperText={errors["pincode"]}
              />

              <FormControl fullWidth required error={!!errors["country"]}>
                <InputLabel id="country-label">{COUNTRY}</InputLabel>
                <Select
                  labelId="country-label"
                  name="country"
                  value={getFieldValue("country") || ""}
                  onChange={handleChange}
                  label={COUNTRY}
                  required
                >
                  {countries.map((country) => (
                    <MenuItem key={country._id} value={country._id}>
                      {country.name.en}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors["country"]}</FormHelperText>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth required error={!!errors["state"]}>
                <InputLabel id="state-label">{STATE}</InputLabel>
                <Select
                  labelId="state-label"
                  name="state"
                  value={getFieldValue("state") || ""}
                  onChange={handleChange}
                  label={STATE}
                  required
                >
                  {getFieldValue("country") ? (
                    states.find(
                      (state) => state.country._id === getFieldValue("country")
                    ) ? (
                      states
                        .filter(
                          (state) =>
                            state.country._id === getFieldValue("country")
                        )
                        .map((state) => (
                          <MenuItem key={state._id} value={state._id}>
                            {state.name.en}
                          </MenuItem>
                        ))
                    ) : (
                      <MenuItem value="">No states found</MenuItem>
                    )
                  ) : (
                    <MenuItem value="">Select a country first</MenuItem>
                  )}
                </Select>
                <FormHelperText>{errors["state"]}</FormHelperText>
              </FormControl>

              <FormControl fullWidth required error={!!errors["district"]}>
                <InputLabel id="district-label">{DISTRICT}</InputLabel>
                <Select
                  labelId="district-label"
                  name="district"
                  value={getFieldValue("district") || ""}
                  onChange={handleChange}
                  label={DISTRICT}
                  required
                >
                  {getFieldValue("state") ? (
                    states.map(
                      (state) =>
                        state._id === getFieldValue("state") &&
                        (state?.districts?.length > 0 ? (
                          state.districts
                            .sort((a, b) => a.name.en.localeCompare(b.name.en))
                            .map((district) => (
                              <MenuItem key={district._id} value={district._id}>
                                {district.name.en}
                              </MenuItem>
                            ))
                        ) : (
                          <MenuItem value="">No districts found</MenuItem>
                        ))
                    )
                  ) : (
                    <MenuItem value="">Select a state first</MenuItem>
                  )}
                </Select>
                <FormHelperText>{errors["district"]}</FormHelperText>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Latitude"
                name="coordinates.latitude"
                className={styles.urlInputFields}
                value={getFieldValue("coordinates.latitude") || ""}
                onChange={handleChange}
                required
                error={!!errors["coordinates.latitude"]}
                helperText={errors["coordinates.latitude"]}
              />
              <TextField
                label="Longitude"
                name="coordinates.longitude"
                className={styles.urlInputFields}
                value={getFieldValue("coordinates.longitude") || ""}
                onChange={handleChange}
                required
                error={!!errors["coordinates.longitude"]}
                helperText={errors["coordinates.longitude"]}
              />
            </Stack>
          </Stack>
        </Box>
        <Divider variant="middle" className={styles.divider} />
        <Box className={styles.section_container}>
          <Typography variant="h6" gutterBottom>
            Media / मीडिया
          </Typography>
          <Stack spacing={2}>
            <FileUploadPreview
              label="Upload Thumbnail Image / थंबनेल इमेज अपलोड करें"
              id="thumbnail-image-upload"
              fileField="coverImage"
              acceptedTypes={ACCEPTED_IMAGE_TYPES.join(",")}
              allowUrl
              urlField="coverImageUrl"
              getFieldValue={getFieldValue}
              onFileChange={handleFileChange}
              onUrlChange={handleChange}
              error={errors["coverImageUrl"]}
              errors={errors}
            />
            <FileUploadPreview
              label="Gallery Images / गैलरी इमेज अपलोड करें"
              id="gallery-image-upload"
              fileField="gallery"
              acceptedTypes={ACCEPTED_IMAGE_TYPES.join(",")}
              multiple
              allowUrl
              urlField="galleryUrls"
              getFieldValue={getFieldValue}
              onFileChange={handleFileChange}
              onUrlChange={handleChange}
              onRemoveItem={handleRemoveGalleryItem}
              onAddUrl={handleAddGalleryUrl}
              error={errors["gallery"]}
              errors={errors}
            />
          </Stack>
        </Box>

        <Divider variant="middle" className={styles.divider} />

        <Box className={styles.section_container}>
          <Typography variant="h6" gutterBottom>
            Historical & Cultural Significance/ ऐतिहासिक और सांस्कृतिक महत्व
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Established Year"
                name="establishedYear.en"
                className={styles.urlInputFields}
                value={getFieldValue("establishedYear.en") || ""}
                onChange={handleChange}
                error={!!errors["establishedYear.en"]}
                helperText={errors["establishedYear.en"]}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Importance of the Tirth "
                name="importance.en"
                className={styles.urlInputFields}
                value={getFieldValue("importance.en") || ""}
                onChange={handleChange}
                error={!!errors["importance.en"]}
                helperText={errors["importance.en"]}
              />
              <TextField
                label="तीर्थ का महत्व"
                name="importance.hi"
                className={styles.urlInputFields}
                value={getFieldValue("importance.hi") || ""}
                onChange={handleChange}
                error={!!errors["importance.hi"]}
                helperText={errors["importance.hi"]}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="History"
                name="history.en"
                className={styles.urlInputFields}
                value={getFieldValue("history.en") || ""}
                onChange={handleChange}
              />
              <TextField
                label="इतिहास "
                name="history.hi"
                className={styles.urlInputFields}
                value={getFieldValue("history.hi") || ""}
                onChange={handleChange}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {sector.length > 0 && (
                <MultiInput
                  options={sector}
                  onChange={handleChange}
                  value={
                    sector.length > 0
                      ? sector.filter((s) =>
                        (getFieldValue("sect") || []).includes(s._id)
                      )
                      : []
                  }
                  name="sect"
                  label="Sector*"
                  type="object"
                  select
                />
              )}
            </Stack>
          </Stack>
        </Box>

        <Divider variant="middle" className={styles.divider} />

        {/* Facilities and Surroundings */}
        <Box className={styles.section_container}>
          <Typography variant="h6" gutterBottom>
            Facilities and Surroundings / सुविधाएं और आस-पास का क्षेत्र
          </Typography>
          <Stack spacing={2}>
            <Stack spacing={2}>
              <ReactQuillEditor
                value={getFieldValue("visitingHours.en") || ""}
                setValue={(value) =>
                  handleChange({
                    target: {
                      name: "visitingHours.en",
                      value,
                    },
                  })
                }
                placeholder="Write Visiting details here..."
                error={!!errors["visitingHours.en"]}
                helperText={"visitingHours.en"}
              />
              <ReactQuillEditor
                value={getFieldValue("visitingHours.hi") || ""}
                setValue={(value) =>
                  handleChange({
                    target: {
                      name: "visitingHours.hi",
                      value,
                    },
                  })
                }
                placeholder="यहाँ विज़िट विवरण लिखें..."
                error={!!errors["visitingHours.hi"]}
                helperText={"visitingHours.hi"}
              />
            </Stack>
          </Stack>
        </Box>

        <Divider variant="middle" className={styles.divider} />

        <Box className={styles.section_container}>
          <Typography variant="h6" gutterBottom>
            Contact Information / संपर्क जानकारी
          </Typography>
          <Stack spacing={2}>
            <Typography variant="h6" gutterBottom>
              General Contact
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Phone"
                name="contactDetails.phone"
                value={getFieldValue("contactDetails.phone") || ""}
                onChange={handleChange}
                error={!!errors["contactDetails.phone"]}
                helperText={errors["contactDetails.phone"]}
              />
              <TextField
                fullWidth
                label="Email"
                email
                name="contactDetails.email"
                value={getFieldValue("contactDetails.email") || ""}
                onChange={handleChange}
                error={!!errors["contactDetails.email"]}
                helperText={errors["contactDetails.email"]}
              />
              <TextField
                fullWidth
                label="Website"
                name="contactDetails.website"
                value={getFieldValue("contactDetails.website") || ""}
                onChange={handleChange}
                error={!!errors["contactDetails.website"]}
                helperText={errors["contactDetails.website"]}
              />
            </Stack>
          </Stack>

          <Stack spacing={2}>
            <Typography variant="h6" gutterBottom>
              Contact Person
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Name"
                name="contactPerson.name.en"
                value={getFieldValue("contactPerson.name.en") || ""}
                onChange={handleChange}
                error={!!errors["contactPerson.name.en"]}
                helperText={errors["contactPerson.name.en"]}
              />
              <TextField
                fullWidth
                label="नाम"
                name="contactPerson.name.hi"
                value={getFieldValue("contactPerson.name.hi") || ""}
                onChange={handleChange}
                error={!!errors["contactPerson.name.hi"]}
                helperText={errors["contactPerson.name.hi"]}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Phone"
                name="contactPerson.phone"
                value={getFieldValue("contactPerson.phone") || ""}
                onChange={handleChange}
                error={!!errors["contactPerson.phone"]}
                helperText={errors["contactPerson.phone"]}
              />
              <TextField
                fullWidth
                label="Email"
                email
                name="contactPerson.email"
                value={getFieldValue("contactPerson.email") || ""}
                onChange={handleChange}
                error={!!errors["contactPerson.email"]}
                helperText={errors["contactPerson.email"]}
              />
            </Stack>
          </Stack>
        </Box>
        <Divider variant="middle" className={styles.divider} />

        <SocialMediaField
          getFieldValue={getFieldValue}
          handleChange={handleChange}
          errors={errors}
        />

        <Divider variant="middle" className={styles.divider} />
        {/* Meta Details Section */}
        <Box className={styles.section_container}>
          <Typography variant="h6" gutterBottom>
            {META_DETAILS}
          </Typography>
          <MetaFields
            getFieldValue={getFieldValue}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
          />
        </Box>
        <Divider variant="middle" className={styles.divider} />

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          {!isEditMode && (
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={handleResetForm}
              sx={{ flex: 1 }}
            >
              Reset Form
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            disabled={isLoading}
            sx={{ flex: !isEditMode ? 2 : 1 }}
          >
            {isEditMode ? UPDATE_TIRTH_BUTTON : ADD_NEW_TIRTH_BUTTON}
          </Button>
        </Stack>
      </form>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Restore Draft Confirmation Dialog */}
      <CustomDialog
        open={draftDialogOpen}
        title="Restore Draft?"
        content="We found a saved draft for this form. Do you want to restore it?"
        primaryText="Restore"
        secondaryText="Delete Draft"
        onPrimaryClick={handleRestoreDraft}
        onSecondaryClick={handleDeleteDraft}
        onClose={() => setDraftDialogOpen(false)}
      />
    </Paper>
  );
};

export default TirthForm;
